import { Avatar, Divider, Link, Stack, Typography } from '@mui/material';
import { useAppSelector, useIsDesktop } from 'Hooks';
import { useUpdatePersonalDetailsDictionary } from 'Hooks/useUpdatePersonalDetailsDictionary';
import { AccordionSection } from '../AccordionSection';
import { MailAddress } from './MailAddress';
import { useFormContext } from 'react-hook-form';
import { ToggleButtons } from 'Components/HookFormInputs/ToggleButtons';
import { TextInput } from 'Components/HookFormInputs/TextInput';
import { CheckboxInput } from 'Components/CheckboxInput';
import { LeftArrow } from 'Images';
import { AutoCompleteInput } from 'Components/HookFormInputs/AutoCompleteInput';
import { sanitizeLettersNumbersAndSpacialChars } from 'Utils/sanitizeLettersNumbersAndSpacialChars';
import { sanitizeNumbers, sanitizeNumbersLength } from 'Utils/sanitizeNumbers';
import { ResetButton } from '../ResetButton';
import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { CustomFieldset } from 'Components';
import { DynamicArrow } from 'Components/DynamicArrow';

interface IAddressProps {
  isDifferentAddress: boolean;
  setIsDifferentAddress: (value: boolean) => void;
  isReset: React.MutableRefObject<boolean>;
}

export const Address = ({
  isDifferentAddress,
  setIsDifferentAddress,
  isReset,
}: IAddressProps) => {
  const {
    register,
    formState: { errors, defaultValues },
    watch,
    reset,
    setValue,
  } = useFormContext();
  const dictionary = useUpdatePersonalDetailsDictionary().address;
  const generalOptions = useAppSelector(
    (generalOptions) => generalOptions.generalOptions.generalOptions
  );

  const isDesktop = useIsDesktop();

  const isAbroad = watch('addresses.0.isAbroad');

  const params = useParams();

  useEffect(() => {
    if (isAbroad) {
      const options = {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      };
      setValue('addresses.0.country', '', options);
      setValue('addresses.0.abroadCity', '', options);
      setValue('addresses.0.street', '', options);
      setValue('addresses.0.houseNum', '', options);
      setValue('addresses.0.zipCode', '', options);
    } else reset({ addresses: [{ ...defaultValues?.addresses[0], isAbroad }] });
  }, [isAbroad]);

  return (
    <AccordionSection
      defaultExpanded={params.sectionId === '3'}
      title={dictionary.title}
      subtitle={dictionary.subtitle}
    >
      <Stack alignItems={'center'}>
        <Stack maxWidth={isDesktop ? '400px' : 'initial'} gap='20px'>
          <CustomFieldset>
            <legend>{dictionary.inputLabel1}</legend>
            <Typography
              alignSelf='flex-start'
              component='legend'
              display='block'
              mb='10px'
            >
              {dictionary.inputLabel1}
            </Typography>
            <ToggleButtons
              option1={{
                id: false,
                value: dictionary.israel,
              }}
              option2={{
                id: true,
                value: dictionary.otherCountry,
              }}
              registerName='addresses.0.isAbroad'
            />
          </CustomFieldset>
          <CustomFieldset>
            <Typography
              alignSelf='flex-start'
              component='legend'
              display='block'
              mb='10px'
            >
              {dictionary.address.title}
            </Typography>
            <legend>{dictionary.address.title}</legend>
            {isAbroad && (
              <AutoCompleteInput
                name='addresses.0.country'
                label={dictionary.address.inputLabel1Abroad}
                options={
                  generalOptions?.countries?.map((country) => ({
                    id: country.id,
                    value: country.value,
                  })) || []
                }
                defaultValue={generalOptions?.countries?.find(
                  (country) => country.id === defaultValues?.[0]?.country
                )}
                isReset={isReset}
                areaLabel={dictionary.address.countryDescription}
              />
            )}
            {!isAbroad && (
              <AutoCompleteInput
                name='addresses.0.city'
                label={dictionary.address.inputLabel1}
                options={
                  generalOptions?.cities?.map((city) => ({
                    id: city.id,
                    value: city.value,
                  })) || []
                }
                defaultValue={generalOptions?.cities?.find(
                  (city) => city.id === defaultValues?.[0]?.city
                )}
                isReset={isReset}
                areaLabel={dictionary.address.cityDescription}
              />
            )}
            {isAbroad && (
              <TextInput
                label={dictionary.address.inputLabel2Abroad}
                register={register('addresses.0.abroadCity', {
                  onChange: (e) => sanitizeLettersNumbersAndSpacialChars(e, 30),
                })}
                errors={(errors as any)?.addresses?.[0]?.abroadCity}
                shrink={(watch('addresses.0.abroadCity') || '')?.length > 0}
              />
            )}
            <TextInput
              label={dictionary.address.inputLabel2}
              register={register('addresses.0.street', {
                onChange: (e) => sanitizeLettersNumbersAndSpacialChars(e, 50),
              })}
              errors={(errors as any)?.addresses?.[0]?.street}
              shrink={(watch('addresses.0.street') || '')?.length > 0}
            />
            <Stack
              minWidth={isDesktop ? '400px' : undefined}
              flexDirection='row'
              justifyContent='space-between'
              gap={isDesktop ? '20px' : '16px'}
            >
              <TextInput
                label={dictionary.address.inputLabel3}
                register={register('addresses.0.houseNum', {
                  onChange: (e) => sanitizeNumbersLength(e, 6),
                })}
                errors={(errors as any)?.addresses?.[0]?.houseNum}
                shrink={(watch('addresses.0.houseNum') || '')?.length > 0}
                isNumbersOnly={true}
              />
              {isAbroad && (
                <TextInput
                  label={dictionary.address.inputLabel5}
                  register={register('addresses.0.zipCode', {
                    onChange: (e) => sanitizeNumbersLength(e, 7),
                  })}
                  errors={(errors as any)?.addresses?.[0]?.zipCode}
                  shrink={(watch('addresses.0.zipCode') || '')?.length > 0}
                  isNumbersOnly={true}
                />
              )}
              {!isAbroad && (
                <TextInput
                  label={dictionary.address.inputLabel4}
                  register={register('addresses.0.apartmentNum', {
                    onChange: (e) => sanitizeNumbersLength(e, 6),
                  })}
                  errors={(errors as any)?.addresses?.[0]?.apartmentNum}
                  shrink={(watch('addresses.0.apartmentNum') || '')?.length > 0}
                  isNumbersOnly={true}
                />
              )}
            </Stack>
            {!isAbroad && (
              <TextInput
                label={dictionary.address.inputLabel5}
                register={register('addresses.0.zipCode', {
                  onChange: (e) => sanitizeNumbersLength(e, 7),
                })}
                errors={(errors as any)?.addresses?.[0]?.zipCode}
                shrink={(watch('addresses.0.zipCode') || '')?.length > 0}
                isNumbersOnly={true}
              />
            )}
          </CustomFieldset>
          {!isAbroad && (
            <Link
              href={dictionary.findZipCodeLink}
              rel='noopener noreferrer'
              target='_blank'
              underline={'hover'}
            >
              <Stack direction={'row'} alignItems={'center'}>
                <Typography variant={'body3'}>
                  {dictionary.locateZipCode}
                </Typography>
                <DynamicArrow color={'blue'} rotate={90} />
              </Stack>
            </Link>
          )}
          <CustomFieldset sx={{ gap: 0 }}>
            <CheckboxInput
              checked={isDifferentAddress}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setIsDifferentAddress(e.target.checked)
              }
              label={dictionary.shippingAddressIsDifferent}
              containerStyle={isDesktop ? {} : { alignItems: 'flex-start' }}
              textVariant={'body3'}
            />
            {isDifferentAddress && <MailAddress isReset={isReset} />}
          </CustomFieldset>
          <ResetButton
            onClick={() => {
              isReset.current = true;
              reset(defaultValues);
              setIsDifferentAddress(false);
            }}
            resetCategoryAriaText={dictionary.reset}
          />
        </Stack>
      </Stack>
    </AccordionSection>
  );
};
